import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
	ArrowRight,
	Sparkles,
	ShieldCheck,
	Bot,
	BookOpen,
	Lock,
	ListChecks,
	Activity,
	HeartHandshake,
	Award,
	CheckCircle2,
	Mail,
	MessageCircle,
	Send,
	Menu,
	X,
} from 'lucide-react';
import api, { formatApiError } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import Logo from '@/components/Logo';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';

const NAV_LINKS = [
	{ href: '#features', label: 'Features' },
	{ href: '#how', label: 'How it works' },
	{ href: '#mission', label: 'Mission' },
	{ href: '#pricing', label: 'Pricing' },
	{ href: '#faq', label: 'FAQ' },
	{ href: '#contact', label: 'Contact' },
];

const FEATURES = [
	{ icon: ListChecks, title: '6-pillar assessment', body: 'A 24-question conversation across Income Stability, Emergency Preparedness, Insurance, Child Preparedness, Financial Continuity and Future Readiness.' },
	{ icon: Activity, title: 'Family Resilience Score', body: 'A single 0–100 number that captures how prepared your family really is — with a friendly category label.' },
	{ icon: Bot, title: 'AI Family Coach', body: 'Personalized tasks, kind missions, monthly reviews. It educates and motivates — it never sells you a product.' },
	{ icon: Award, title: 'Levels & badges', body: 'A gentle Duolingo-style journey: Explorer → Planner → Protector → Guardian → Resilience Leader → Family Champion.' },
	{ icon: Lock, title: 'Private Family Vault', body: 'Zero-knowledge, client-side encryption for your family blueprint, legacy notes and continuity instructions.' },
	{ icon: BookOpen, title: 'Educational Library', body: 'Beautiful lessons on emergency funds, insurance, EPF, NPS, index investing, child education and more — never selling products.' },
];

const HOW = [
	{ title: 'Assess', body: 'Answer 24 gentle questions in about 6 minutes. Get your Family Resilience Score.' },
	{ title: 'Get your plan', body: 'The AI Family Coach turns your weakest pillars into small, doable tasks with score gains.' },
	{ title: 'Improve monthly', body: 'Track missions, mark tasks done and watch your score climb from Explorer to Family Champion.' },
];

const FAQ = [
	{
		q: 'Is MyFamShield a financial advisor or insurance seller?',
		a: 'No. MyFamShield is a Family Preparedness and Resilience Platform. We never sell stocks, mutual funds, or insurance. We educate, coach and motivate — nothing more.',
	},
	{
		q: 'What is the Family Resilience Score?',
		a: 'A single 0–100 number, computed from a 24-question assessment across 6 pillars. It reflects how prepared your family is for common life disruptions — not your net worth.',
	},
	{
		q: 'How is my data protected?',
		a: 'Premium Vault data is encrypted on your device using AES-GCM with a passphrase only you know. Our servers only ever see encrypted blobs. Admins, developers and support engineers cannot read it.',
	},
	{
		q: 'What happens if I forget my Vault passphrase?',
		a: 'If you lose both your passphrase and your recovery key, your encrypted information cannot be recovered — by design. Please download your Recovery Package and keep it safe and offline.',
	},
	{
		q: 'Do I need to pay to try MyFamShield?',
		a: 'No. The assessment, score, progress tracking and educational library are free forever. Premium unlocks the Family Resilience Coach™ personalized tasks, monthly reports and the encrypted Vault.',
	},
	{
		q: 'Which countries do you serve?',
		a: 'MyFamShield works anywhere. Indian users can pay with UPI/cards via Razorpay in ₹. International users pay in $ via Stripe.',
	},
	{
		q: 'How often should we retake the assessment?',
		a: 'Once a month is ideal. Preparedness is a small habit repeated — not a one-time event. Retaking every 30 days lets you see the compounding effect.',
	},
];

export default function Landing() {
	const { user } = useAuth();
	const navigate = useNavigate();
	const [mobileOpen, setMobileOpen] = useState(false);
	const [form, setForm] = useState({ name: '', email: '', subject: '', message: '', website: '' });
	const [submitting, setSubmitting] = useState(false);
	const [submitted, setSubmitted] = useState(false);

	const submitContact = async (e) => {
		e.preventDefault();
		if (form.website) return; // honeypot
		if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
			toast.error('Please share your name, email and message.');
			return;
		}
		setSubmitting(true);
		try {
			await api.post('/contact', {
				name: form.name.trim(),
				email: form.email.trim(),
				subject: form.subject.trim(),
				message: form.message.trim(),
				source: 'contact',
				page: window.location.pathname + window.location.hash,
			});
			setSubmitted(true);
			setForm({ name: '', email: '', subject: '', message: '', website: '' });
			toast.success("Thank you — we've received your note.");
		} catch (err) {
			toast.error(formatApiError(err, 'Something went wrong. Please try again.'));
		} finally {
			setSubmitting(false);
		}
	};

	const goStart = () => navigate(user ? '/dashboard' : '/register');
	const goSignIn = () => navigate(user ? '/dashboard' : '/login');

	return (
		<div className="min-h-screen bg-white text-slate-900 overflow-x-hidden">
			{/* NAV */}
			<header className="sticky top-0 z-40 backdrop-blur bg-white/85 border-b border-slate-200">
				<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 h-16 flex items-center justify-between">
					<Link to="/" data-testid="landing-nav-logo"><Logo /></Link>
					<nav className="hidden md:flex items-center gap-7">
						{NAV_LINKS.map((n) => (
							<a key={n.href} href={n.href} className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors">
								{n.label}
							</a>
						))}
					</nav>
					<div className="hidden md:flex items-center gap-2">
						<Button variant="ghost" className="rounded-full h-10 px-4" onClick={goSignIn} data-testid="landing-signin-button">Sign in</Button>
						<Button className="rounded-full h-10 px-5 bg-[#1E3A8A] hover:bg-[#1E40AF]" onClick={goStart} data-testid="landing-cta-primary">
							Take the free assessment <ArrowRight className="w-4 h-4 ml-1.5" />
						</Button>
					</div>
					<button type="button" className="md:hidden p-2 rounded-full hover:bg-slate-100" onClick={() => setMobileOpen((v) => !v)} aria-label="Menu">
						{mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
					</button>
				</div>
				{mobileOpen && (
					<div className="md:hidden border-t border-slate-200 bg-white">
						<div className="px-4 py-4 flex flex-col gap-2">
							{NAV_LINKS.map((n) => (
								<a key={n.href} href={n.href} onClick={() => setMobileOpen(false)} className="px-3 py-2 rounded-2xl text-sm font-medium text-slate-700 hover:bg-slate-50">{n.label}</a>
							))}
							<div className="flex gap-2 pt-2">
								<Button variant="outline" className="rounded-full flex-1" onClick={goSignIn}>Sign in</Button>
								<Button className="rounded-full flex-1 bg-[#1E3A8A] hover:bg-[#1E40AF]" onClick={goStart}>Get started</Button>
							</div>
						</div>
					</div>
				)}
			</header>

			{/* HERO */}
			<section className="relative overflow-hidden">
				<div className="absolute inset-0 -z-10"
					style={{ background: 'radial-gradient(1200px 500px at 15% -20%, rgba(30,58,138,0.10) 0%, transparent 55%), radial-gradient(1200px 500px at 85% -10%, rgba(16,185,129,0.14) 0%, transparent 60%), linear-gradient(180deg,#F8FAFC 0%,#FFFFFF 60%)' }}
				/>
				<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 pt-16 pb-20 lg:pt-24 lg:pb-28 grid lg:grid-cols-[1.15fr,1fr] gap-14 items-center">
					<div className="animate-fade-in-up">
						<div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-800 text-xs font-bold tracking-wider uppercase">
							<Sparkles className="w-3.5 h-3.5" /> Family Resilience Platform
						</div>
						<h1 className="mt-6 font-heading text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight text-slate-900">
							If something unexpected happens tomorrow,{' '}
							<span className="text-transparent bg-clip-text" style={{ backgroundImage: 'linear-gradient(90deg,#1E3A8A,#10B981)' }}>
								how prepared is your family?
							</span>
						</h1>
						<p className="mt-6 text-lg text-slate-600 max-w-xl leading-relaxed">
							MyFamShield helps families measure, improve and continuously strengthen their preparedness — job loss, medical events, child education, retirement, continuity. Not just finances.
						</p>
						<div className="mt-8 flex flex-wrap items-center gap-3">
							<Button className="h-12 px-6 rounded-full bg-[#1E3A8A] hover:bg-[#1E40AF] font-semibold text-white shadow-md hover:-translate-y-0.5 transition-all" onClick={goStart} data-testid="landing-hero-cta">
								Take the free assessment <ArrowRight className="w-4 h-4 ml-1.5" />
							</Button>
							<a href="#how" className="h-12 px-5 rounded-full border border-slate-300 hover:border-slate-400 font-semibold text-slate-700 hover:bg-slate-50 inline-flex items-center transition-colors" data-testid="landing-hero-secondary">
								See how it works
							</a>
						</div>
						<div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-slate-500">
							<div className="inline-flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600" /> Free assessment, forever</div>
							<div className="inline-flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600" /> No product selling</div>
							<div className="inline-flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-600" /> Zero-knowledge Vault</div>
						</div>
					</div>

					{/* Hero visual — score gauge card */}
					<div className="hidden lg:flex justify-center">
						<div className="relative">
							<div className="absolute -inset-4 rounded-[36px]" style={{ background: 'linear-gradient(135deg, rgba(30,58,138,0.15), rgba(16,185,129,0.15))', filter: 'blur(24px)' }} />
							<div className="relative w-[420px] card-soft p-8">
								<div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">Family Resilience Score</div>
								<div className="mt-4 flex items-center gap-6">
									<svg width="140" height="140" viewBox="0 0 140 140">
										<circle cx="70" cy="70" r="60" stroke="#E2E8F0" strokeWidth="14" fill="none" />
										<circle cx="70" cy="70" r="60" stroke="#10B981" strokeWidth="14" strokeLinecap="round" fill="none" strokeDasharray={`${(0.72) * 2 * Math.PI * 60} ${2 * Math.PI * 60}`} transform="rotate(-90 70 70)" />
										<text x="70" y="72" textAnchor="middle" fontFamily="Outfit" fontSize="42" fontWeight="700" fill="#0F172A">72</text>
										<text x="70" y="92" textAnchor="middle" fontSize="10" fill="#64748B">out of 100</text>
									</svg>
									<div>
										<div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-blue-50 text-blue-800 text-[10px] font-bold uppercase tracking-widest">
											<span className="w-1.5 h-1.5 rounded-full bg-blue-500" /> Strong
										</div>
										<div className="mt-3 text-xs font-bold uppercase tracking-widest text-slate-400">Next level</div>
										<div className="text-sm font-semibold text-slate-800">Guardian in 13 points</div>
										<div className="mt-4 text-xs font-bold uppercase tracking-widest text-slate-400">This month&apos;s mission</div>
										<div className="text-sm font-semibold text-slate-800">Update all nominees</div>
									</div>
								</div>
								<div className="mt-6 space-y-2">
									{[
										{ pill: 'Emergency fund', gain: 5, done: true },
										{ pill: 'Update nominees', gain: 4, done: false },
										{ pill: 'Family discussion', gain: 3, done: false },
									].map((t) => (
										<div key={t.pill} className="flex items-center gap-3 p-2.5 rounded-2xl bg-slate-50">
											<div className={`w-5 h-5 rounded-full grid place-items-center ${t.done ? 'bg-emerald-500 text-white' : 'bg-white border-2 border-slate-200'}`}>
												{t.done && <CheckCircle2 className="w-3 h-3" />}
											</div>
											<div className="text-sm text-slate-700 flex-1 truncate">{t.pill}</div>
											<div className="text-xs font-bold text-emerald-700 tabular-nums">+{t.gain}</div>
										</div>
									))}
								</div>
							</div>
						</div>
					</div>
				</div>
			</section>

			{/* HOW IT WORKS */}
			<section id="how" className="py-20 lg:py-24 bg-white">
				<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
					<div className="max-w-2xl">
						<div className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-600">How it works</div>
						<h2 className="mt-3 text-3xl sm:text-4xl font-heading font-bold tracking-tight">Three tiny habits, done monthly.</h2>
						<p className="mt-3 text-slate-600 leading-relaxed">A resilient family isn&apos;t built in a weekend — it&apos;s built by showing up, again and again, in small ways.</p>
					</div>
					<div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
						{HOW.map((s, i) => (
							<div key={s.title} className="relative card-soft p-7">
								<div className="text-6xl font-heading font-bold text-transparent bg-clip-text" style={{ backgroundImage: 'linear-gradient(135deg,#1E3A8A,#10B981)' }}>0{i + 1}</div>
								<h3 className="mt-2 font-heading text-xl font-semibold text-slate-900">{s.title}</h3>
								<p className="mt-2 text-slate-600 text-sm leading-relaxed">{s.body}</p>
							</div>
						))}
					</div>
				</div>
			</section>

			{/* FEATURES */}
			<section id="features" className="py-20 lg:py-24 bg-slate-50">
				<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
					<div className="max-w-2xl">
						<div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-800">Features</div>
						<h2 className="mt-3 text-3xl sm:text-4xl font-heading font-bold tracking-tight">Everything a modern family needs to feel prepared.</h2>
					</div>
					<div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
						{FEATURES.map((f) => (
							<div key={f.title} className="card-soft p-7 hover:-translate-y-0.5 transition-transform">
								<div className="w-11 h-11 rounded-2xl bg-emerald-100 text-emerald-700 grid place-items-center">
									<f.icon className="w-5 h-5" />
								</div>
								<h3 className="mt-4 font-heading text-lg font-semibold text-slate-900">{f.title}</h3>
								<p className="mt-2 text-sm text-slate-600 leading-relaxed">{f.body}</p>
							</div>
						))}
					</div>
				</div>
			</section>

			{/* MISSION / ABOUT */}
			<section id="mission" className="py-20 lg:py-24 bg-white">
				<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 grid lg:grid-cols-2 gap-14 items-center">
					<div>
						<div className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-600">Mission</div>
						<h2 className="mt-3 text-3xl sm:text-4xl font-heading font-bold tracking-tight">Every family deserves to be prepared for life&apos;s uncertainties.</h2>
						<p className="mt-5 text-slate-600 leading-relaxed">Life doesn&apos;t always go according to plan. Families should be prepared for job loss, medical emergencies, disability, financial disruption, child education, retirement and continuity. MyFamShield helps you measure, improve and continuously strengthen your preparedness — one small mission at a time.</p>
						<p className="mt-4 text-slate-600 leading-relaxed">We are not a financial advisor, investment platform, insurance marketplace, or will service. We are a coach. We educate. We motivate. We celebrate small wins.</p>
						<div className="mt-8 grid grid-cols-2 gap-4">
							{[
								{ icon: HeartHandshake, text: 'Trusted family coach' },
								{ icon: ShieldCheck, text: 'Privacy-first design' },
								{ icon: Sparkles, text: 'Duolingo-style progress' },
								{ icon: Award, text: 'Meaningful levels' },
							].map((v) => (
								<div key={v.text} className="flex items-center gap-3 rounded-2xl bg-slate-50 p-4">
									<v.icon className="w-5 h-5 text-emerald-600" />
									<span className="text-sm font-semibold text-slate-800">{v.text}</span>
								</div>
							))}
						</div>
					</div>
					<div className="relative">
						<div className="absolute -inset-6 rounded-[36px]" style={{ background: 'linear-gradient(135deg, rgba(30,58,138,0.10), rgba(16,185,129,0.16))', filter: 'blur(20px)' }} />
						<blockquote className="relative card-soft p-8 lg:p-10">
							<div className="text-5xl leading-none text-emerald-500 font-heading">&ldquo;</div>
							<p className="mt-2 text-lg font-heading text-slate-800 leading-snug">
								Small steps, repeated with love, become a sturdy shield for your family.
							</p>
							<div className="mt-6 text-sm text-slate-500">Every interaction inside MyFamShield answers one question: how can this family become more resilient than they were yesterday?</div>
						</blockquote>
					</div>
				</div>
			</section>

			{/* PRICING TEASER */}
			<section id="pricing" className="py-20 lg:py-24 bg-slate-50">
				<div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-10 text-center">
					<div className="text-xs font-bold uppercase tracking-[0.2em] text-blue-800">Pricing</div>
					<h2 className="mt-3 text-3xl sm:text-4xl font-heading font-bold tracking-tight">Free forever, or all-in for the price of a coffee.</h2>
					<p className="mt-3 text-slate-600 max-w-xl mx-auto">The assessment, the score and the library are free forever. Premium unlocks personalized coaching, tasks, monthly reports and the encrypted Family Vault.</p>
					<div className="mt-10 flex justify-center">
						<Link to={user ? '/subscription' : '/register'}>
							<Button className="rounded-full h-12 px-6 bg-[#1E3A8A] hover:bg-[#1E40AF] font-semibold">
								See plans <ArrowRight className="w-4 h-4 ml-2" />
							</Button>
						</Link>
					</div>
				</div>
			</section>

			{/* FAQ */}
			<section id="faq" className="py-20 lg:py-24 bg-white">
				<div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-10">
					<div className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-600">FAQ</div>
					<h2 className="mt-3 text-3xl sm:text-4xl font-heading font-bold tracking-tight">Questions, gently answered.</h2>
					<Accordion type="single" collapsible className="mt-10">
						{FAQ.map((f, i) => (
							<AccordionItem key={i} value={`item-${i}`} className="border-b border-slate-200 py-1">
								<AccordionTrigger data-testid={`landing-faq-${i}`} className="text-left font-heading text-lg font-semibold text-slate-900 py-4 hover:no-underline">
									{f.q}
								</AccordionTrigger>
								<AccordionContent className="text-slate-600 leading-relaxed pb-5">
									{f.a}
								</AccordionContent>
							</AccordionItem>
						))}
					</Accordion>
				</div>
			</section>

			{/* CONTACT */}
			<section id="contact" className="py-20 lg:py-24 bg-slate-50">
				<div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 grid lg:grid-cols-[1fr,1.15fr] gap-12">
					<div>
						<div className="text-xs font-bold uppercase tracking-[0.2em] text-emerald-600">Contact</div>
						<h2 className="mt-3 text-3xl sm:text-4xl font-heading font-bold tracking-tight">Let&apos;s talk.</h2>
						<p className="mt-4 text-slate-600 leading-relaxed">Have a question, want to partner, or want to share feedback? We read every note.</p>
						<div className="mt-8 space-y-4">
							<a href="mailto:myfamshield@gmail.com" className="flex items-center gap-3 group">
								<div className="w-11 h-11 rounded-2xl bg-emerald-100 text-emerald-700 grid place-items-center">
									<Mail className="w-5 h-5" />
								</div>
								<div>
									<div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">Email</div>
									<div className="font-heading font-semibold text-slate-900 group-hover:text-emerald-700 transition-colors">myfamshield@gmail.com</div>
								</div>
							</a>
							<div className="flex items-center gap-3">
								<div className="w-11 h-11 rounded-2xl bg-blue-100 text-blue-800 grid place-items-center">
									<MessageCircle className="w-5 h-5" />
								</div>
								<div>
									<div className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">Response time</div>
									<div className="font-heading font-semibold text-slate-900">Within 1 business day</div>
								</div>
							</div>
						</div>
					</div>

					<div className="card-soft p-6 sm:p-8" data-testid="landing-contact-form">
						{submitted ? (
							<div className="py-6 text-center">
								<div className="mx-auto w-14 h-14 rounded-2xl bg-emerald-100 text-emerald-700 grid place-items-center">
									<CheckCircle2 className="w-7 h-7" />
								</div>
								<h3 className="mt-4 font-heading text-2xl font-semibold text-slate-900">Thank you.</h3>
								<p className="mt-2 text-slate-600 text-sm max-w-md mx-auto">We&apos;ve received your note and will reply within one business day. If it was urgent, replying to our acknowledgement email lands straight in our inbox.</p>
								<Button variant="outline" className="rounded-full mt-6" onClick={() => setSubmitted(false)}>Send another</Button>
							</div>
						) : (
							<form onSubmit={submitContact} className="space-y-4">
								<div className="grid sm:grid-cols-2 gap-4">
									<div>
										<Label htmlFor="c-name">Name</Label>
										<Input id="c-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="h-11 rounded-xl mt-1.5" placeholder="Your name" data-testid="landing-contact-name" />
									</div>
									<div>
										<Label htmlFor="c-email">Email</Label>
										<Input id="c-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-11 rounded-xl mt-1.5" placeholder="you@family.com" data-testid="landing-contact-email" />
									</div>
								</div>
								<div>
									<Label htmlFor="c-subject">Subject (optional)</Label>
									<Input id="c-subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="h-11 rounded-xl mt-1.5" placeholder="What's this about?" data-testid="landing-contact-subject" />
								</div>
								<div>
									<Label htmlFor="c-message">Message</Label>
									<Textarea id="c-message" required minLength={5} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="rounded-xl mt-1.5 min-h-[140px]" placeholder="Tell us what's on your mind." data-testid="landing-contact-message" />
								</div>
								{/* honeypot — hidden from real users */}
								<input
									type="text"
									name="website"
									value={form.website}
									onChange={(e) => setForm({ ...form, website: e.target.value })}
									tabIndex={-1}
									autoComplete="off"
									aria-hidden="true"
									className="hidden"
								/>
								<Button type="submit" disabled={submitting} className="rounded-full h-12 bg-[#1E3A8A] hover:bg-[#1E40AF] w-full sm:w-auto" data-testid="landing-contact-submit">
									{submitting ? 'Sending…' : (<><Send className="w-4 h-4 mr-2" /> Send message</>)}
								</Button>
								<p className="text-xs text-slate-500">By sending, you agree that we&apos;ll use your email to reply to you. We do not sell or share your data.</p>
							</form>
						)}
					</div>
				</div>
			</section>

			{/* FINAL CTA */}
			<section className="py-20 lg:py-24 bg-white">
				<div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-10 text-center">
					<h2 className="text-3xl sm:text-4xl font-heading font-bold tracking-tight">Ready to see where your family stands?</h2>
					<p className="mt-3 text-slate-600 max-w-xl mx-auto">Six minutes. Twenty-four gentle questions. Your Family Resilience Score, forever free.</p>
					<div className="mt-8">
						<Button className="h-12 px-6 rounded-full bg-[#1E3A8A] hover:bg-[#1E40AF] font-semibold text-white" onClick={goStart} data-testid="landing-final-cta">
							Take the free assessment <ArrowRight className="w-4 h-4 ml-1.5" />
						</Button>
					</div>
				</div>
			</section>

			<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
				<Footer />
			</div>
		</div>
	);
}
